/**
 * Перевод отказов бэкенда в тексты для игрока, подающего заявку по ссылке.
 *
 * Две особенности контракта, из-за которых нужен именно словарь по тексту, а не
 * ветвление по статусу:
 *
 * 1. `ExceptionListener` (`imgame-backend/src/EventListener/ExceptionListener.php`)
 *    отдаёт осмысленный статус только наследникам `HttpException`. Всё остальное —
 *    ошибки JWT, формат даты, лимит заявок, тип файла — приходит как **500** с
 *    телом `{"code":500,"message":"Internal error: <текст исключения>"}`.
 * 2. Превышение 5 МБ режет nginx (`client_max_body_size 5M`), и в ответ прилетает
 *    **HTML**, а не JSON. Разбирать `message` там нечего — нужен статус.
 *
 * Сообщения бэкенда — английские строки исключений, часть с подставленными
 * значениями, поэтому сопоставление идёт по подстроке.
 */

/** Максимальный размер тела запроса — `client_max_body_size` в nginx. */
export const MAX_REQUEST_SIZE_MB = 5;

export interface ApplicationErrorResult {
  message: string;
  /**
   * Заявка на бэкенде уже создана, несмотря на ошибку.
   *
   * Контроллер создаёт и коммитит заявку, и только потом грузит фото в S3
   * (`ChampionshipApplicationsController.php:193-204`). Падение загрузки даёт
   * клиенту 500 при существующей заявке — предлагать отправить её заново нельзя.
   */
  applicationCreated: boolean;
}

const GENERIC = 'Не удалось отправить заявку. Попробуйте позже или обратитесь к организатору';
const NETWORK = 'Нет связи с сервером. Проверьте интернет и попробуйте ещё раз';
const BROKEN_LINK = 'Ссылка повреждена. Запросите новую у организатора';
const PHOTO_FAILED =
  'Заявка отправлена, но фото загрузить не удалось. Организатор добавит его вручную';

interface ErrorRule {
  /** Подстроки в `message`; достаточно совпадения любой. */
  match: string[];
  /** Текст либо функция, достающая из сообщения подставленные значения. */
  text: string | ((backendMessage: string) => string);
  applicationCreated?: boolean;
}

const RULES: ErrorRule[] = [
  // --- Токен ---------------------------------------------------------------
  {
    match: ['Missing required parameter "token"', 'Token not found'],
    text: 'Ссылка неполная. Запросите новую у организатора',
  },
  {
    // Срок жизни токена = дате окончания приёма заявок (`APPLICATION_END_DATE`).
    match: ['Expired JWT Token'],
    text: 'Срок подачи заявок истёк. Обратитесь к организатору за новой ссылкой',
  },
  {
    match: ['Invalid JWT Token', 'Unable to verify', 'invalid token', 'JWT Token not found'],
    text: BROKEN_LINK,
  },

  // --- Поля формы ----------------------------------------------------------
  { match: ['parameter "number"'], text: 'Игровой номер должен быть числом от 1 до 99' },
  { match: ['parameter "position"'], text: 'Выберите амплуа из списка' },
  { match: ['parameter "name"'], text: 'Укажите имя' },
  { match: ['parameter "surname"'], text: 'Укажите фамилию' },
  { match: ['parameter "birthDate"'], text: 'Укажите дату рождения' },
  {
    match: ['Accepted format dd.mm.yyyy'],
    text: 'Проверьте дату рождения — она должна быть в формате 25.05.1990',
  },

  // --- Фото ----------------------------------------------------------------
  {
    // Проверка типа стоит до создания заявки — значит заявки нет, остаёмся на форме.
    match: ['File type not allowed'],
    text: 'Формат фото не поддерживается. Подойдут JPG, PNG или WebP',
  },
  {
    // А это уже после создания заявки: сама заявка сохранена, потерялось только фото.
    match: ['Could not upload file', 'Could not get the content of the file', 'uploadToS3('],
    text: PHOTO_FAILED,
    applicationCreated: true,
  },

  // --- Состояние турнира ---------------------------------------------------
  {
    match: ['not in championship'],
    text: 'Команда больше не участвует в этом турнире. Обратитесь к организатору',
  },
  {
    match: ['applications limit exceeded'],
    text: (backendMessage) => {
      const limit = backendMessage.match(/only make (\d+) applications/)?.[1];
      return limit
        ? `В команде уже максимум игроков (${limit}). Обратитесь к организатору`
        : 'В команде уже максимум игроков. Обратитесь к организатору';
    },
  },
];

interface HttpErrorShape {
  status?: number;
  message?: string;
  hasResponse: boolean;
}

/**
 * Разбор ошибки axios без импорта его типов: `isAxiosError` достаточно, а полный
 * `AxiosError` в домене только тащит зависимость от версии клиента.
 */
function readHttpError(error: unknown): HttpErrorShape {
  if (typeof error !== 'object' || error === null) return { hasResponse: false };

  const { response } = error as { response?: { status?: number; data?: unknown } };
  if (!response) return { hasResponse: false };

  const { data } = response;
  const message =
    typeof data === 'string' ? data : typeof data === 'object' && data !== null ? (data as { message?: string }).message : undefined;

  return { status: response.status, message, hasResponse: true };
}

function matchRule(backendMessage: string): ApplicationErrorResult | undefined {
  const rule = RULES.find((item) => item.match.some((needle) => backendMessage.includes(needle)));
  if (!rule) return undefined;

  return {
    message: typeof rule.text === 'function' ? rule.text(backendMessage) : rule.text,
    applicationCreated: rule.applicationCreated ?? false,
  };
}

/** Текст для отказа при отправке заявки (`POST /application/create-from-token-v2`). */
export function getApplicationErrorMessage(error: unknown): ApplicationErrorResult {
  const { status, message, hasResponse } = readHttpError(error);

  if (!hasResponse) return { message: NETWORK, applicationCreated: false };

  // nginx обрывает запрос до приложения и отвечает HTML — разбирать нечего.
  if (status === 413) {
    return {
      message: `Фото слишком большое. Выберите файл меньше ${MAX_REQUEST_SIZE_MB} МБ`,
      applicationCreated: false,
    };
  }

  const matched = message ? matchRule(message) : undefined;
  return matched ?? { message: GENERIC, applicationCreated: false };
}

/**
 * Ответа не было вовсе: сеть, а не отказ бэкенда.
 *
 * Отличается от прочих ошибок тем, что ссылка, скорее всего, цела — проблема на
 * стороне пользователя, и её имеет смысл предложить исправить.
 */
export function isNetworkError(error: unknown): boolean {
  return !readHttpError(error).hasResponse;
}

/** Текст для отказа при расшифровке ссылки (`GET /application/decode-token`). */
export function getTokenErrorMessage(error: unknown): string {
  const { message, hasResponse } = readHttpError(error);

  if (!hasResponse) return NETWORK;
  if (!message) return BROKEN_LINK;

  // Турнир или команду удалили после выпуска ссылки — ссылка цела, данных нет.
  if (/^Championship \d+ not found/.test(message)) {
    return 'Турнир не найден. Возможно, он удалён — обратитесь к организатору';
  }
  if (/^Team \d+ not found/.test(message)) {
    return 'Команда не найдена. Возможно, она удалена — обратитесь к организатору';
  }

  return matchRule(message)?.message ?? BROKEN_LINK;
}

/**
 * Ссылка протухла: срок жизни токена равен дате окончания приёма заявок.
 *
 * Отдельная функция, потому что это единственный отказ, на который страница
 * отвечает не тостом, а собственным экраном.
 */
export function isExpiredTokenError(error: unknown): boolean {
  const { message } = readHttpError(error);
  return Boolean(message?.includes('Expired JWT Token'));
}
